/**
 * モーショングラフィックス (プロンプト入力モードの 3 タブ目) を今使えるか
 * (docs/features/motion-graphics-mode-plan.md §5.1・§5.3・§6.1)。
 *
 * `GET /api/motion-layered/availability` → `{enabled, reason?, credits?, unit_description?}`
 * - enabled: 暫定ゲートを通り、料金行 (credit_rate_config) がある
 * - reason: 'restricted' (backend の暫定ゲート) / 'rate_not_configured' (料金行なし)
 * レンダラーの状態は含まない (利用者決定 2026-10-01: 描画は生成を押した後なので、起動待ちでタブを消さない。
 * レンダラーに届かないときは generate の受付が 503 MOTION_RENDERER_UNAVAILABLE を返す)。
 *
 * fail-closed: 取得に失敗した・形が崩れている・enabled なのに料金が読めない、はすべて使えない扱いにする
 * (生成ボタンを押せない。タブの表示は shouldShowMotionGraphicsTab)。
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';

export type MotionLayeredUnavailableReason =
  | 'restricted'
  | 'rate_not_configured'
  /** FE 側の理由: 取得に失敗した / 応答の形が崩れている */
  | 'fetch_failed';

export type MotionLayeredAvailability =
  | { enabled: true; credits: number; unitDescription: string | null }
  | { enabled: false; reason: MotionLayeredUnavailableReason; credits: number | null };

const KNOWN_REASONS: ReadonlySet<string> = new Set(['restricted', 'rate_not_configured']);

/** 取得に失敗したときの値 (fail-closed) */
export const MOTION_LAYERED_AVAILABILITY_FAILED: MotionLayeredAvailability = {
  enabled: false, reason: 'fetch_failed', credits: null,
};

function toCredits(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** 応答を検証して MotionLayeredAvailability にする。enabled は true (真偽値) のときだけ認め、料金が読めなければ使えない扱い。 */
export function parseMotionLayeredAvailability(raw: unknown): MotionLayeredAvailability {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return MOTION_LAYERED_AVAILABILITY_FAILED;
  const data = raw as Record<string, unknown>;
  const credits = toCredits(data.credits);
  if (data.enabled === true) {
    // 料金を出せない (= 利用者に消費ポイントを示せない) まま生成させない
    if (credits === null) return { enabled: false, reason: 'rate_not_configured', credits: null };
    return {
      enabled: true,
      credits,
      unitDescription: typeof data.unit_description === 'string' ? data.unit_description : null,
    };
  }
  const reason = typeof data.reason === 'string' && KNOWN_REASONS.has(data.reason)
    ? data.reason as MotionLayeredUnavailableReason
    : 'fetch_failed';
  return { enabled: false, reason, credits };
}

export async function fetchMotionLayeredAvailability(): Promise<MotionLayeredAvailability> {
  const response = await apiClient.get<unknown>('/motion-layered/availability');
  return parseMotionLayeredAvailability(response.data);
}

/** 使えない理由の利用者向け文言 (理由ごとにここ 1 箇所で定義する)。 */
export const MOTION_LAYERED_UNAVAILABLE_MESSAGES: Record<MotionLayeredUnavailableReason, string> = {
  restricted: 'モーショングラフィックスは現在ご利用いただけません',
  rate_not_configured: '料金が設定されていないため、現在ご利用いただけません',
  fetch_failed: '利用状況を確認できないため、現在ご利用いただけません。しばらくしてからもう一度お試しください',
};

export function motionLayeredUnavailableMessage(availability: MotionLayeredAvailability | null | undefined): string | null {
  if (!availability) return MOTION_LAYERED_UNAVAILABLE_MESSAGES.fetch_failed;
  return availability.enabled ? null : MOTION_LAYERED_UNAVAILABLE_MESSAGES[availability.reason];
}

/** react-query のキー。利用者ごとに結果が違う (backend の暫定ゲート) ので user_uid / 組織を含める。 */
export const MOTION_LAYERED_AVAILABILITY_QUERY_KEY = ['motion-layered-availability'] as const;

export function motionLayeredAvailabilityQueryKey(userKey: string | null, organizationId: string | null) {
  return [...MOTION_LAYERED_AVAILABILITY_QUERY_KEY, userKey, organizationId] as const;
}

/** 暫定ゲート・料金行はすぐには変わらないので数分キャッシュする */
export const MOTION_LAYERED_AVAILABILITY_STALE_MS = 5 * 60 * 1000;
/** 使えない (enabled=false・取得失敗) 間は短い間隔で取り直す (料金行の適用後にリロードなしで開く) */
export const MOTION_LAYERED_AVAILABILITY_RETRY_INTERVAL_MS = 60 * 1000;

/**
 * useQuery / queryClient.fetchQuery で共用する設定 (キー・取得・鮮度)。fetchQuery はキャッシュが新鮮ならそれを返す。
 */
export function motionLayeredAvailabilityQueryOptions(userKey: string | null, organizationId: string | null) {
  return {
    queryKey: motionLayeredAvailabilityQueryKey(userKey, organizationId),
    queryFn: fetchMotionLayeredAvailability,
    staleTime: MOTION_LAYERED_AVAILABILITY_STALE_MS,
  };
}

/** refetchInterval: 使えない間 (enabled=false・取得失敗) だけ 1 分ごとに取り直す。使える間は staleTime に任せる */
export function motionLayeredAvailabilityRefetchInterval(state: {
  status: string;
  data?: MotionLayeredAvailability;
}): number | false {
  if (state.status === 'error') return MOTION_LAYERED_AVAILABILITY_RETRY_INTERVAL_MS;
  if (state.data && !state.data.enabled) return MOTION_LAYERED_AVAILABILITY_RETRY_INTERVAL_MS;
  return false;
}

/**
 * availability を読む。enabled=false (ブランドのフラグが無い・未ログイン) なら取りに行かず data=undefined。
 * - pending: 取りに行っていて、まだ結果が無い (タブの表示を決めかねている間。呼び出し側は motion を寄せない)
 * - availability: 結果。失敗は MOTION_LAYERED_AVAILABILITY_FAILED (fail-closed)。取りに行かないときは null
 */
export function useMotionLayeredAvailability({
  enabled,
  userKey,
  organizationId,
}: {
  enabled: boolean;
  userKey: string | null;
  organizationId: string | null;
}): { availability: MotionLayeredAvailability | null; pending: boolean } {
  const query = useQuery({
    ...motionLayeredAvailabilityQueryOptions(userKey, organizationId),
    enabled,
    // App.tsx の既定は refetchOnMount: false (リロードまで取り直さない) なので、ここはマウント時に stale なら取り直す
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => motionLayeredAvailabilityRefetchInterval(q.state),
    retry: 1,
  });
  if (!enabled) return { availability: null, pending: false };
  // 前回の結果がある状態で再取得に失敗したときは、react-query の既定どおり前回の結果を使い続ける (意図的)。
  // 一時的な通信失敗でタブが出たり消えたりしないため。料金行の削除や暫定ゲートは受付の 503 / 403 で取り直す
  if (query.data) return { availability: query.data, pending: false };
  if (query.isError) return { availability: MOTION_LAYERED_AVAILABILITY_FAILED, pending: false };
  return { availability: null, pending: true };
}
